import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AppUserTimeEntryMethodService, TimeEntryMethods } from '../../services/app-user-time-entry-method.service';
import { Observable, of, filter, take, map } from 'rxjs';

interface TimeEntryPolicy {
  /** Per-method redirect map keyed by enum string value. If current method key exists -> redirect; else allow. */
  redirectMap?: Partial<Record<TimeEntryMethods, string>>;
}

/**
 * If the current time entry method appears as a key in redirectMap, navigation is redirected to the associated path. 
 * Otherwise navigation is allowed.
 */
@Injectable({ providedIn: 'root' })
export class TimeEntryMethodGuard implements CanActivate {
  static readonly CLOCK_ENTRY_PAGES_DEFAULT_REDIRECT_MAP = { BLOCK_ENTRY: 'app/timecards', NOT_TIME_USER: 'app/forms' } as const;
  static readonly BLOCK_ENTRY_PAGES_DEFAULT_REDIRECT_MAP = { CLOCK_ENTRY: 'app/home', NOT_TIME_USER: 'app/forms' } as const;

  constructor(
    private router: Router, 
    private appUserTimeEntryMethodService: AppUserTimeEntryMethodService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const policy = route.data?.['timeEntryPolicy'] as TimeEntryPolicy | undefined;
    if (!policy) return of(true);

    return this.appUserTimeEntryMethodService.timeEntryMethod$.pipe(
      filter(method => method !== TimeEntryMethods.UNKNOWN),
      take(1),
      map(methodValue => {
        const redirectTarget = policy.redirectMap?.[methodValue];
        if (!redirectTarget) return true; // Allowed

        const normalizedTarget = redirectTarget.startsWith('/') ? redirectTarget : `/${redirectTarget}`;
        const currentPath = state.url.split('?')[0];
        if (currentPath === normalizedTarget) return true; // Already at target

        return this.router.parseUrl(normalizedTarget);
      })
    );
  }
}
